import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Pencil, Plus, Search, ShieldCheck, Sparkles, Trash2, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PersonnelBulkUpload } from "@/components/PersonnelBulkUpload";
import { FileRefInput } from "@/components/FileRefInput";
import { CsvInput } from "@/components/CsvInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const LBL = "text-[10px] uppercase tracking-[0.1em] text-[#6B7280]";
const blank = {
  full_name: "", employee_id: "", category: "", designation: "", department: "Haematology",
  email: "", phone: "", joining_date: "", status: "Active", registration_no: "", council: "",
  licence_expiry: "", authorised_tests: [], authorised_analyzers: [], induction_done: false,
  induction_date: "", cv_file: "", remarks: "", qualifications: [], trainings: [], competencies: [],
};
const emptyQual = { degree: "", university: "", year: "", registration_no: "", council: "", file: "" };
const emptyTrain = { topic: "", training_date: "", trainer: "", hours: "", training_type: "Internal", outcome: "", file: "" };
const emptyComp = { assessment_date: "", scope: "", method: "Direct Observation", outcome: "Competent", assessor: "", next_due_date: "", remarks: "", file: "" };

const RowSet = ({ title, hint, rows, cols, onChange, empty, testid }) => (
  <div className="rounded-md border border-[#E5E7EB] p-3" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div>
        <p className="font-heading font-bold text-xs text-[#111827]">{title}</p>
        <p className="text-[11px] text-[#6B7280]">{hint}</p>
      </div>
      <Button type="button" variant="outline" size="sm" onClick={() => onChange([...rows, { ...empty }])}
        data-testid={`${testid}-add`}>
        <Plus className="h-3.5 w-3.5 mr-1" /> Add
      </Button>
    </div>
    {rows.length === 0 && <p className="text-xs text-[#6B7280] mt-2">Nothing recorded yet.</p>}
    <div className="mt-2 space-y-2">
      {rows.map((row, i) => (
        <div key={i} className="grid sm:grid-cols-12 gap-2 items-center" data-testid={`${testid}-row-${i}`}>
          {cols.map((c) => (
            <div key={c.key} className={c.span}>
              <p className={LBL}>{c.label}</p>
              {c.options ? (
                <select value={row[c.key] || ""} data-testid={`${testid}-${c.key}-${i}`}
                  onChange={(e) => onChange(rows.map((x, idx) => (idx === i ? { ...x, [c.key]: e.target.value } : x)))}
                  className="mt-1 h-9 w-full rounded-md border border-[#E5E7EB] bg-white px-2 text-xs">
                  {c.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <Input value={row[c.key] || ""} type={c.type || "text"} data-testid={`${testid}-${c.key}-${i}`}
                  onChange={(e) => onChange(rows.map((x, idx) => (idx === i ? { ...x, [c.key]: e.target.value } : x)))}
                  className="mt-1 h-9 text-xs" />
              )}
            </div>
          ))}
          <button type="button" onClick={() => onChange(rows.filter((_, idx) => idx !== i))}
            data-testid={`${testid}-remove-${i}`}
            className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#C92A2A] mt-4 h-fit"><Trash2 className="h-4 w-4" /></button>
          <div className="mt-4 h-fit">
            <FileRefInput value={row.file} testId={`${testid}-file-${i}`} label="File"
              onChange={(ref) => onChange(rows.map((x, idx) => (idx === i ? { ...x, file: ref } : x)))} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default function Personnel() {
  const { user, options } = useAuth();
  const [meta, setMeta] = useState(null);
  const [people, setPeople] = useState([]);
  const [ready, setReady] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [joinedFrom, setJoinedFrom] = useState("");
  const [joinedTo, setJoinedTo] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);

  const extract = async (file) => {
    if (!file) return;
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/personnel/extract", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => {
        const next = { ...prev };
        const rowKey = {
          qualifications: (r) => `${r.degree || ""}|${r.year || ""}`.toLowerCase(),
          trainings: (r) => `${r.topic || ""}|${r.training_date || ""}`.toLowerCase(),
          competencies: (r) => `${r.assessment_date || ""}|${r.scope || ""}`.toLowerCase(),
        };
        Object.entries(data).forEach(([k, v]) => {
          if (rowKey[k]) {
            const have = new Set((prev[k] || []).map(rowKey[k]));
            next[k] = [...(prev[k] || [])];
            (v || []).forEach((r) => {
              const key = rowKey[k](r);
              if (key !== "|" && !have.has(key)) { have.add(key); next[k].push(r); }
            });
          } else if (Array.isArray(v)) {
            next[k] = Array.from(new Set([...(prev[k] || []), ...v]));
          } else if (v && !String(prev[k] || "").trim()) {
            next[k] = v;
          }
        });
        return next;
      });
      toast.success(`Read ${file.name} — fields filled in, please review before saving`);
    } catch (e) { toast.error(errMsg(e)); } finally { setExtracting(false); }
  };

  const load = useCallback(async () => {
    try {
      const [p, rd] = await Promise.all([
        api.get("/personnel", { params: { q, category, joined_from: joinedFrom, joined_to: joinedTo } }),
        api.get("/personnel/readiness"),
      ]);
      setPeople(p.data);
      setReady(rd.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [q, category, joinedFrom, joinedTo]);

  useEffect(() => { api.get("/personnel/meta").then(({ data }) => setMeta(data))
    .catch((e) => console.warn("personnel meta unavailable:", e)); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openNew = () => { setForm({ ...blank }); setEditing("new"); };
  const openEdit = (p) => { setForm({ ...blank, ...p }); setEditing(p.person_id); };
  const set = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const save = async () => {
    if (!form.full_name.trim() || !form.category) return toast.error("Name and category are required");
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.readiness;
      if (editing === "new") await api.post("/personnel", payload);
      else await api.put(`/personnel/${editing}`, payload);
      toast.success(editing === "new" ? "Personnel record created" : "Personnel record updated");
      setEditing(null);
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete the personnel file for ${p.full_name}?`)) return;
    try {
      await api.delete(`/personnel/${p.person_id}`);
      toast.success("Personnel record deleted");
      await load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const isSignatory = (c) => (meta?.signatory_categories || []).includes(c);
  const canManage = user?.role === "admin" || user?.role === "quality_manager" ||
    people.some((p) => p.email && p.email === user?.email && p.category === "Quality Manager");

  return (
    <div data-testid="personnel-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-[#111827]">Personnel</h1>
          <p className="text-sm text-[#6B7280] mt-1 max-w-2xl">
            ISO 15189 personnel files — qualifications, training, competency assessment and authorisation
            scope. Active consultants and senior residents appear automatically in the report sign-out
            dropdown; technologists and technicians feed the Performed By lists.
            {!canManage && " Records are read-only — ask an administrator or the quality manager to update them."}
          </p>
        </div>
        {canManage && (
          <Button onClick={openNew} data-testid="add-person-btn" className="bg-[#002FA7] hover:bg-[#00237C] text-white">
            <Plus className="h-4 w-4 mr-1.5" /> Add personnel
          </Button>
        )}
      </div>

      {canManage && (
        <div className="mt-5">
          <PersonnelBulkUpload onDone={load} />
        </div>
      )}

      {ready && (
        <div className="mt-5 rounded-md border border-[#E5E7EB] bg-white p-4" data-testid="personnel-readiness">
          <div className="flex flex-wrap gap-6">
            <div>
              <p className={LBL}>On roll</p>
              <p className="font-heading text-2xl font-extrabold text-[#111827]" data-testid="readiness-total">{ready.total}</p>
            </div>
            <div>
              <p className={LBL}>File complete</p>
              <p className="font-heading text-2xl font-extrabold text-[#2E7D32]" data-testid="readiness-compliant">{ready.compliant}</p>
            </div>
            <div>
              <p className={LBL}>Needs attention</p>
              <p className="font-heading text-2xl font-extrabold text-[#B45309]" data-testid="readiness-gaps">{ready.with_gaps.length}</p>
            </div>
            <div>
              <p className={LBL}>Authorised signatories</p>
              <p className="font-heading text-2xl font-extrabold text-[#002FA7]" data-testid="readiness-signatories">{ready.signatories}</p>
            </div>
          </div>
          {ready.with_gaps.length > 0 && (
            <div className="mt-3 divide-y divide-[#E5E7EB]">
              {ready.with_gaps.slice(0, 5).map((g) => (
                <div key={g.person_id} className="py-2 flex flex-wrap items-center gap-2 text-xs"
                  data-testid={`gap-${g.person_id}`}>
                  <AlertTriangle className="h-3.5 w-3.5 text-[#B45309]" />
                  <span className="text-[#111827] font-medium">{g.full_name}</span>
                  <span className="text-[#6B7280]">{g.category}</span>
                  <span className="text-[#B45309]">{g.gaps.join(" · ")}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="personnel-search"
            placeholder="Search name, employee ID or registration no…" className="pl-9 bg-white" />
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="personnel-category-filter"
          className="h-10 rounded-md border border-[#E5E7EB] bg-white px-3 text-sm">
          <option value="">All categories</option>
          {(meta?.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <Input type="date" value={joinedFrom} onChange={(e) => setJoinedFrom(e.target.value)}
          className="h-10 w-40 bg-white" data-testid="personnel-joined-from" title="Joined on or after" />
        <Input type="date" value={joinedTo} onChange={(e) => setJoinedTo(e.target.value)}
          className="h-10 w-40 bg-white" data-testid="personnel-joined-to" title="Joined on or before" />
      </div>

      <div className="mt-4 rounded-md border border-[#E5E7EB] bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="personnel-table">
          <thead className="bg-[#F9FAFB]">
            <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-[#6B7280]">
              <th className="px-4 py-2.5">Person</th>
              <th className="px-4 py-2.5">Category</th>
              <th className="px-4 py-2.5">Qualification</th>
              <th className="px-4 py-2.5">Competency</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {people.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-[#6B7280]" data-testid="personnel-empty">
                No personnel files yet. Add your staff to build the ISO 15189 competency record.</td></tr>
            )}
            {people.map((p) => (
              <tr key={p.person_id} className="hover:bg-[#F9FAFB]" data-testid={`person-row-${p.person_id}`}>
                <td className="px-4 py-2.5">
                  <p className="font-medium text-[#111827] flex items-center gap-1.5">
                    {p.full_name}
                    {isSignatory(p.category) && p.status === "Active" && (
                      <ShieldCheck className="h-3.5 w-3.5 text-[#002FA7]" title="Authorised signatory" />
                    )}
                  </p>
                  <p className="text-xs text-[#6B7280]">
                    {[p.employee_id, p.designation, p.department].filter(Boolean).join(" · ") || "—"}
                  </p>
                </td>
                <td className="px-4 py-2.5 text-xs">{p.category}</td>
                <td className="px-4 py-2.5 text-xs">
                  {p.qualifications?.length
                    ? `${p.qualifications.map((q2) => q2.degree).filter(Boolean).join(", ")}`
                    : <span className="text-[#B45309]">Not recorded</span>}
                  {p.registration_no && <p className="text-[#6B7280]">Reg. {p.registration_no}</p>}
                </td>
                <td className="px-4 py-2.5 text-xs">
                  {p.readiness?.last_assessed
                    ? (<>
                        <span className={p.readiness.competency_outcome === "Competent" ? "text-[#2E7D32]" : "text-[#B45309]"}>
                          {p.readiness.competency_outcome}
                        </span>
                        <p className="text-[#6B7280]">
                          {p.readiness.last_assessed}{p.readiness.competency_due ? ` · due ${p.readiness.competency_due}` : ""}
                        </p>
                      </>)
                    : <span className="text-[#B45309]">Not assessed</span>}
                </td>
                <td className="px-4 py-2.5">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] ${
                    p.status === "Active" ? "bg-[#ECFDF5] text-[#2E7D32]"
                      : p.status === "Resigned" || p.status === "Retired" ? "bg-[#F3F4F6] text-[#374151]"
                      : "bg-[#FFFBEB] text-[#B45309]"}`} data-testid={`person-status-${p.person_id}`}>
                    {p.status}
                  </span>
                  {p.readiness?.gaps?.length === 0 && (
                    <CheckCircle2 className="inline h-3.5 w-3.5 ml-1.5 text-[#2E7D32]" title="File complete" />
                  )}
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex justify-end gap-1">
                    {canManage && (
                      <button onClick={() => openEdit(p)} data-testid={`person-edit-${p.person_id}`}
                        className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#002FA7]"><Pencil className="h-4 w-4" /></button>
                    )}
                    {user?.role === "admin" && (
                      <button onClick={() => remove(p)} data-testid={`person-delete-${p.person_id}`}
                        className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#C92A2A]"><Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-white max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="person-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">
              {editing === "new" ? "Add personnel" : `Personnel file — ${form.full_name}`}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#6B7280]">
              Everything ISO 15189 expects in a personnel file: identity, qualification, induction, training,
              competency assessment and the tests or analysers the person is authorised to run.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-md border border-dashed border-[#002FA7]/40 bg-[#EFF6FF] p-3 flex flex-wrap items-center gap-3">
            <div className="min-w-0">
              <p className="font-heading font-bold text-xs text-[#111827]">Fill this file from a document</p>
              <p className="text-[11px] text-[#6B7280]">
                Upload a CV, appointment letter, degree or council certificate, training record or competency
                sheet — the details, qualifications, training and competency rows are read and filled in for
                you. Nothing you have already typed is overwritten.
              </p>
            </div>
            <label className="ml-auto inline-flex items-center h-9 px-3 rounded-md border border-[#002FA7] bg-white text-xs cursor-pointer text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors"
              data-testid="person-extract-label">
              {extracting
                ? <span className="flex items-center"><Sparkles className="h-3.5 w-3.5 mr-1.5 animate-pulse" /> Reading…</span>
                : <span className="flex items-center"><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Extract &amp; auto-fill</span>}
              <input type="file" className="hidden" data-testid="person-extract-input"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.csv,.heic,.heif"
                onChange={(e) => { extract(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <Label className={LBL}>Full name *</Label>
              <Input value={form.full_name} onChange={set("full_name")} className="mt-1" data-testid="person-name-input" />
            </div>
            <div>
              <Label className={LBL}>Employee ID</Label>
              <Input value={form.employee_id} onChange={set("employee_id")} className="mt-1" data-testid="person-empid-input" />
            </div>
            <div>
              <Label className={LBL}>Category *</Label>
              <select value={form.category} onChange={set("category")} data-testid="person-category-input"
                className="mt-1 h-10 w-full rounded-md border border-[#E5E7EB] bg-white px-2 text-sm">
                <option value="">Select category</option>
                {(meta?.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label className={LBL}>Designation</Label>
              <Input value={form.designation} onChange={set("designation")} className="mt-1" data-testid="person-designation-input" />
            </div>
            <div>
              <Label className={LBL}>Department</Label>
              <Input value={form.department} onChange={set("department")} className="mt-1" data-testid="person-department-input" />
            </div>
            <div>
              <Label className={LBL}>Email</Label>
              <Input value={form.email} onChange={set("email")} className="mt-1" data-testid="person-email-input" />
            </div>
            <div>
              <Label className={LBL}>Phone</Label>
              <Input value={form.phone} onChange={set("phone")} className="mt-1" data-testid="person-phone-input" />
            </div>
            <div>
              <Label className={LBL}>Date of joining</Label>
              <Input type="date" value={form.joining_date} onChange={set("joining_date")} className="mt-1" data-testid="person-joining-input" />
            </div>
            <div>
              <Label className={LBL}>Status</Label>
              <select value={form.status} onChange={set("status")} data-testid="person-status-input"
                className="mt-1 h-10 w-full rounded-md border border-[#E5E7EB] bg-white px-2 text-sm">
                {(meta?.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label className={LBL}>Council registration no.</Label>
              <Input value={form.registration_no} onChange={set("registration_no")} className="mt-1" data-testid="person-regno-input" />
            </div>
            <div>
              <Label className={LBL}>Council / board</Label>
              <Input value={form.council} onChange={set("council")} className="mt-1" data-testid="person-council-input" />
            </div>
            <div>
              <Label className={LBL}>Registration valid till</Label>
              <Input type="date" value={form.licence_expiry} onChange={set("licence_expiry")} className="mt-1" data-testid="person-licence-input" />
            </div>
            <div className="sm:col-span-2">
              <Label className={LBL}>Authorised tests (comma separated)</Label>
              <CsvInput value={form.authorised_tests} data-testid="person-tests-input"
                onChange={(v) => setForm((p) => ({ ...p, authorised_tests: v }))}
                placeholder={(options.test_name || []).slice(0, 3).join(", ")} className="mt-1" />
            </div>
            <div className="sm:col-span-2">
              <Label className={LBL}>Authorised analysers (comma separated)</Label>
              <CsvInput value={form.authorised_analyzers} data-testid="person-analyzers-input"
                onChange={(v) => setForm((p) => ({ ...p, authorised_analyzers: v }))}
                className="mt-1" />
            </div>
            <label className="flex items-center gap-2 text-xs text-[#374151] self-end pb-2">
              <input type="checkbox" checked={!!form.induction_done} data-testid="person-induction-done"
                onChange={(e) => setForm((p) => ({ ...p, induction_done: e.target.checked }))} />
              Induction completed
            </label>
            <div>
              <Label className={LBL}>Induction date</Label>
              <Input type="date" value={form.induction_date} onChange={set("induction_date")} className="mt-1" data-testid="person-induction-date" />
            </div>
            <div>
              <Label className={LBL}>Remarks</Label>
              <Input value={form.remarks} onChange={set("remarks")} className="mt-1" data-testid="person-remarks-input" />
            </div>
            <div>
              <Label className={LBL}>CV / primary document</Label>
              <div className="mt-1">
                <FileRefInput value={form.cv_file} testId="person-cv-file" label="Attach CV"
                  onChange={(ref) => setForm((p) => ({ ...p, cv_file: ref }))} />
              </div>
            </div>
          </div>

          <RowSet title="Qualifications" testid="person-qualifications"
            hint="Degrees, diplomas and council registrations held."
            rows={form.qualifications} empty={emptyQual}
            onChange={(rows) => setForm((p) => ({ ...p, qualifications: rows }))}
            cols={[
              { key: "degree", label: "Degree", span: "sm:col-span-3" },
              { key: "university", label: "University / Board", span: "sm:col-span-4" },
              { key: "year", label: "Year", span: "sm:col-span-2" },
              { key: "registration_no", label: "Registration no.", span: "sm:col-span-2" },
            ]} />

          <RowSet title="Training record" testid="person-trainings"
            hint="Internal and external training, CME and refresher sessions."
            rows={form.trainings} empty={emptyTrain}
            onChange={(rows) => setForm((p) => ({ ...p, trainings: rows }))}
            cols={[
              { key: "topic", label: "Topic", span: "sm:col-span-4" },
              { key: "training_date", label: "Date", span: "sm:col-span-2", type: "date" },
              { key: "trainer", label: "Trainer", span: "sm:col-span-2" },
              { key: "hours", label: "Hours", span: "sm:col-span-1" },
              { key: "training_type", label: "Type", span: "sm:col-span-2", options: ["Internal", "External", "Online", "Refresher"] },
            ]} />

          <RowSet title="Competency assessment" testid="person-competencies"
            hint="Assessment method, outcome, assessor and when the next review falls due."
            rows={form.competencies} empty={emptyComp}
            onChange={(rows) => setForm((p) => ({ ...p, competencies: rows }))}
            cols={[
              { key: "assessment_date", label: "Assessed on", span: "sm:col-span-2", type: "date" },
              { key: "scope", label: "Scope", span: "sm:col-span-3" },
              { key: "method", label: "Method", span: "sm:col-span-2", options: meta?.competency_methods || [] },
              { key: "outcome", label: "Outcome", span: "sm:col-span-2", options: meta?.competency_outcomes || [] },
              { key: "next_due_date", label: "Next due", span: "sm:col-span-2", type: "date" },
            ]} />

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="person-cancel">
              <X className="h-4 w-4 mr-1" /> Cancel
            </Button>
            <Button onClick={save} disabled={saving} data-testid="person-save"
              className="bg-[#002FA7] hover:bg-[#00237C] text-white">
              {saving ? "Saving…" : "Save personnel file"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
